import { useState, useEffect } from "react";
import { FaCheck, FaEdit, FaTimes } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import ExportOptions from "../../ExportOptions";
import TableSkeleton from "../../TableSkeleton";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import Pagination from "../../Shared/Pagination";

const API_BASE = "https://api.muktihospital.com/api/pharmacy-unit";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const AddUnitModal = ({ isOpen, onClose, onSave }) => {
  const [unitData, setUnitData] = useState({ unitNames: [""] });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setUnitData({ unitNames: [""] });
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const checkDuplicateName = async (unitName) => {
    try {
      const response = await axios.get(`${API_BASE}`, {
        headers: { "x-api-key": API_KEY },
      });
      const existingUnits = response.data.categories || [];
      return existingUnits.some(
        (unit) => unit.name.toLowerCase() === unitName.toLowerCase()
      );
    } catch (error) {
      console.error("Error checking duplicate name:", error);
      return false; // Assume no duplicate in case of error to avoid blocking save
    }
  };

  const validateForm = async () => {
    const newErrors = {};
    let hasError = false;

    for (let index = 0; index < unitData.unitNames.length; index++) {
      const unitName = unitData.unitNames[index];
      if (!unitName.trim()) {
        newErrors[`unitName${index}`] = `Unit Name ${index + 1} is required`;
        hasError = true;
      } else {
        const isDuplicate = await checkDuplicateName(unitName.trim());
        if (isDuplicate) {
          newErrors[`unitName${index}`] = `Unit Name "${unitName.trim()}" already exists`;
          hasError = true;
        }
      }
    }

    setErrors(newErrors);
    if (hasError) {
      toast.error("Please fix the errors in the form", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return !hasError;
  };

  const handleUnitNameChange = (index, value) => {
    const updatedUnitNames = [...unitData.unitNames];
    updatedUnitNames[index] = value;
    setUnitData((prevData) => ({ ...prevData, unitNames: updatedUnitNames }));
    if (value.trim()) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`unitName${index}`];
        return newErrors;
      });
    }
  };

  const addUnitNameField = () => {
    setUnitData((prevData) => ({
      ...prevData,
      unitNames: [...prevData.unitNames, ""],
    }));
  };

  const removeUnitNameField = (index) => {
    setUnitData((prevData) => ({
      ...prevData,
      unitNames: prevData.unitNames.filter((_, i) => i !== index),
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`unitName${index}`];
      return newErrors;
    });
  };

  const handleSave = async () => {
    if (!(await validateForm())) return;

    setIsLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(
        () => source.cancel("Request timed out"),
        10000
      );
      const createdUnits = [];
      for (const unitName of unitData.unitNames) {
        // console.log(`Sending POST request to ${API_BASE}/add with payload:`, {
        //   name: unitName,
        // });
        const response = await axios.post(
          `${API_BASE}/add`,
          { name: unitName.trim() },
          {
            headers: {
              "x-api-key": API_KEY,
              "Content-Type": "application/json",
            },
            cancelToken: source.token,
          }
        );
        if (response.status === 201) createdUnits.push(response.data.unit);
      }
      clearTimeout(timeout);
      toast.success(`${createdUnits.length} unit(s) added successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });
      // console.log("Add units response:", createdUnits);
      onSave(createdUnits);
      setUnitData({ unitNames: [""] });
      setErrors({});
      onClose();
    } catch (error) {
      if (axios.isCancel(error)) {
        toast.error("Unit add request timed out", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        console.error("Add Unit Error:", error.response?.data || error.message);
        toast.error(
          error.response?.data?.error || "Network error! Please try again.",
          { position: "top-right", autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Add Unit</h2>
        </div>
        <div className="p-4">
          <label className="label">
            Unit Name <span className="text-red-500">*</span>
          </label>
          {unitData.unitNames.map((unitName, index) => (
            <div key={index} className="grid grid-cols-5 gap-2 mb-4">
              <div className="col-span-4">
                <input
                  type="text"
                  value={unitName}
                  onChange={(e) => handleUnitNameChange(index, e.target.value)}
                  className={`input-field ${
                    errors[`unitName${index}`] ? "border-red-500" : ""
                  }`}
                  placeholder="Unit Name"
                  disabled={isLoading}
                />
                {errors[`unitName${index}`] && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors[`unitName${index}`]}
                  </span>
                )}
              </div>
              {unitData.unitNames.length > 1 && (
                <div className="col-span-1 flex items-end">
                  <button
                    onClick={() => removeUnitNameField(index)}
                    className="p-2 text-red-500 hover:text-red-700"
                    disabled={isLoading}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            onClick={addUnitNameField}
            className="flex items-center mt-3 gap-1 bg-M-primary-color/90 text-sm text-white py-1 px-3 rounded-sm hover:bg-M-primary-color"
            disabled={isLoading}
          >
            Add
          </button>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D]"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
const EditUnitModal = ({ isOpen, onClose, onSave, unit }) => {
  const [unitName, setUnitName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (unit) setUnitName(unit.name || "");
  }, [unit]);

  if (!isOpen) return null;

  const checkDuplicateName = async (unitName, currentId) => {
    try {
      const response = await axios.get(`${API_BASE}`, {
        headers: { "x-api-key": API_KEY },
      });
      const existingUnits = response.data.categories || [];
      return existingUnits.some(
        (u) => u.name.toLowerCase() === unitName.toLowerCase() && u.id !== currentId
      );
    } catch (error) {
      console.error("Error checking duplicate name:", error);
      return false; // Assume no duplicate in case of error to avoid blocking save
    }
  };

  const handleSave = async () => {
    if (!unitName.trim()) {
      setError("Unit Name is required");
      toast.error("Unit name is required", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const isDuplicate = await checkDuplicateName(unitName.trim(), unit.id);
    if (isDuplicate) {
      setError(`Unit Name "${unitName.trim()}" already exists`);
      toast.error(`Unit Name "${unitName.trim()}" already exists`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(
        () => source.cancel("Request timed out"),
        10000
      );
      // console.log(
      //   `Sending PUT request to ${API_BASE}/edit/${unit.id} with payload:`,
      //   { name: unitName }
      // );
      const response = await axios.put(
        `${API_BASE}/edit/${unit.id}`,
        { name: unitName },
        {
          headers: { "x-api-key": API_KEY, "Content-Type": "application/json" },
          cancelToken: source.token,
        }
      );
      clearTimeout(timeout);
      // console.log("Update unit response:", response.data);
      if (response.status === 200) {
        toast.success("Unit updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        onSave(unit.id, response.data.unit);
        onClose();
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        toast.error("Unit update request timed out", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        console.error(
          "Edit Unit Error:",
          error.response?.data || error.message
        );
        toast.error(
          error.response?.data?.error || "Network error! Please try again.",
          { position: "top-right", autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Edit Unit</h2>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <label className="label">
              Unit Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={unitName}
              onChange={(e) => {
                setUnitName(e.target.value);
                setError("");
              }}
              className={`input-field ${error ? "border-red-500" : ""}`}
              placeholder="Unit Name"
              disabled={isLoading}
            />
            {error && (
              <span className="text-red-500 text-xs mt-1">{error}</span>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D]"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Unit = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [unitToEdit, setUnitToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [units, setUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const itemsPerPage = 10;
  const filteredUnits = units.filter((unit) =>
    unit.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredUnits.length / itemsPerPage);
  const currentData = filteredUnits
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((unit, index) => ({
      ...unit,
      serialNumber: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    const fetchUnits = async () => {
    setIsLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(
        () => source.cancel("Request timed out"),
        10000
      );
      // console.log(`Fetching units from ${API_BASE}`);
      const response = await axios.get(API_BASE, {
        headers: { "x-api-key": API_KEY },
        cancelToken: source.token,
      });
      clearTimeout(timeout);
      // console.log("Fetch units response:", response.data);
      const fetchedUnits = response.data.categories || [];
      setUnits(fetchedUnits.sort((a, b) => b.id - a.id));
      setError(null);
    } catch (error) {
      if (axios.isCancel(error)) {
        setError("Unit fetch request timed out");
        toast.error("Unit fetch request timed out", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        console.error(
          "Fetch Units Error:",
          error.response?.data || error.message
        );
        setError(
          error.response?.data?.error || "Network error! Please try again."
        );
        toast.error(
          error.response?.data?.error || "Network error! Please try again.",
          { position: "top-right", autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };
    fetchUnits();
  }, []);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      // console.log(`Adjusting page from ${currentPage} to ${totalPages}`);
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      // console.log("No data, resetting to page 1");
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const handleSelectAll = (e) => {
    setSelectedRows(e.target.checked ? currentData.map((unit) => unit.id) : []);
  };

  const handleRowSelect = (unitId) => {
    setSelectedRows((prev) =>
      prev.includes(unitId)
        ? prev.filter((id) => id !== unitId)
        : [...prev, unitId]
    );
  };

  const handleDelete = async (unitIds) => {
    setIsLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(
        () => source.cancel("Request timed out"),
        10000
      );
      const requests = Array.isArray(unitIds)
        ? unitIds.map((id) =>
            axios.delete(`${API_BASE}/delete/${id}`, {
              headers: { "x-api-key": API_KEY },
              cancelToken: source.token,
            })
          )
        : [
            axios.delete(`${API_BASE}/delete/${unitIds}`, {
              headers: { "x-api-key": API_KEY },
              cancelToken: source.token,
            }),
          ];
      const responses = await Promise.all(requests);
      clearTimeout(timeout);
      // console.log(
      //   "Delete units response:",
      //   responses.map((res) => res.data)
      // );
      setUnits((prev) => prev.filter((unit) => !unitIds.includes(unit.id)));
      setSelectedRows([]);
      toast.success(
        `${
          Array.isArray(unitIds) ? unitIds.length : 1
        } unit(s) deleted successfully!`,
        {
          position: "top-right",
          autoClose: 3000,
        }
      );
    } catch (error) {
      if (axios.isCancel(error)) {
        toast.error("Unit delete request timed out", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        console.error(
          "Delete Units Error:",
          error.response?.data || error.message
        );
        toast.error(
          error.response?.data?.error || "Network error! Please try again.",
          { position: "top-right", autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (unit) => {
    setUnitToEdit(unit);
    setEditModalOpen(true);
  };

  const handleUpdateUnit = (id, updatedUnit) => {
    setUnits((prev) =>
      prev.map((unit) =>
        unit.id === id ? { ...unit, name: updatedUnit.name } : unit
      )
    );
  };

 const handleAddUnit = (unitData) => {
  setUnits((prev) => [...unitData, ...prev]);
};

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["Serial Number", "Unit Name"];
  const dataMapper = (item) => [item.serialNumber, item.name || "-"].join("\t");

  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left print-hidden-in-print",
      skeletonClassName: "my-5 mx-3",
    },
    {
      width: "80px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "200px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "100px",
      height: "16px",
      className: "text-right p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
  ];

  if (error) {
    return (
      <div className="text-center p-4 text-red-600 print-hidden">{error}</div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 flex-1">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Unit List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                disabled={isLoading}
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by unit name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              disabled={isLoading}
            />
            <div className="flex items-center justify-between gap-3 w-full">
              <button
                onClick={() => setAddModalOpen(true)}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
                disabled={isLoading}
              >
                + Add Unit
              </button>
              <div className="relative" style={{ overflow: "visible" }}>
                <ExportOptions
                  data={currentData}
                  dropdownOpen={dropdownOpen}
                  toggleDropdown={toggleDropdown}
                  title="Unit List"
                  headers={headers}
                  dataMapper={dataMapper}
                />
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            {isLoading ? (
              <div className="print-hidden">
                <table className="min-w-[781px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial Number
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Unit Name
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : currentData.length === 0 ? (
              <div className="print-hidden">
                <table className="min-w-[781px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial Number
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Unit Name
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center py-5 text-gray-600 print-hidden"
                      >
                        No units found
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[781px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              selectedRows.length === currentData.length &&
                              currentData.length > 0
                            }
                            disabled={isLoading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="agreement"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial Number
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Unit Name
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((unit, index) => (
                      <tr key={unit.id} className="border-b hover:bg-gray-50">
                        <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                          <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={"unit" + index}
                              className="hidden peer print-hidden"
                              checked={selectedRows.includes(unit.id)}
                              onChange={() => handleRowSelect(unit.id)}
                              disabled={isLoading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={"unit" + index}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                            ></label>
                          </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {unit.serialNumber}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {unit.name || "-"}
                        </td>
                        <td className="p-3 text-right space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => handleEdit(unit)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={isLoading}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([unit.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={isLoading}
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="print-hidden">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={filteredUnits.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="unit"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={`Do you really want to delete ${selectedRows.length} unit?`}
      />
      <AddUnitModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddUnit}
      />
      <EditUnitModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleUpdateUnit}
        unit={unitToEdit}
      />
    </div>
  );
};

export default Unit;
